import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { audits as auditsApi } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import SemaforoBadge from "../components/SemaforoBadge";
import { FileText, ChevronRight, Trash2, AlertTriangle, Edit3 } from "lucide-react";
import { toast } from "sonner";
import { localizedTemplate } from "../lib/i18nContent";

export default function AuditsListPage() {
  const { viewAllStores, user, can } = useApp();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    auditsApi.list({ view_all: viewAllStores }).then(setItems).catch(console.error);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewAllStores]);

  const canContinue = can("edit_audit");
  const canDelete = can("delete_audit");
  const canEdit = can("edit_audit");

  const reopenAudit = async (a) => {
    if (!window.confirm(t("audits_list.confirm_reopen", { store: a.store_name }))) return;
    try {
      await auditsApi.patch(a.id, { status: "in_progress" });
      toast.success(t("audits_list.reopened"));
      navigate(`/audit/${a.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await auditsApi.remove(deleting.id);
      toast.success(t("audits_list.deleted"));
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("audits_list.delete_error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1500px] mx-auto" data-testid="audits-list-page">
      <div>
        <div className="canales-overline">{t("nav.audits")}</div>
        <h1 className="font-display text-4xl md:text-5xl mt-1">{t("audits_list.title")}</h1>
        <p className="text-stone-600 mt-2 text-sm">{t("audits_list.subtitle")}</p>
      </div>

      <Card className="canales-card mt-8 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("common.store")}</TableHead>
              <TableHead>{t("audits_list.auditor")}</TableHead>
              <TableHead>{t("audits_list.start")}</TableHead>
              <TableHead>{t("common.status")}</TableHead>
              <TableHead>{t("common.score")}</TableHead>
              <TableHead className="text-right">{t("findings.col_action")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12 text-stone-500">
                  {t("audits_list.empty")}
                </TableCell>
              </TableRow>
            )}
            {items.map((a) => (
              <TableRow key={a.id} data-testid={`audit-row-${a.id}`}>
                <TableCell className="font-medium">
                  <div>{a.store_name}</div>
                  {a.audit_number && (
                    <div className="text-[10px] text-stone-400 mt-0.5">{a.audit_number}{a.template ? ` · ${localizedTemplate(a.template, i18n.language)}` : ""}</div>
                  )}
                </TableCell>
                <TableCell className="text-stone-600">{a.auditor_name}</TableCell>
                <TableCell className="text-stone-600">{(a.started_at || "").slice(0, 10)}</TableCell>
                <TableCell>
                  <span
                    className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${
                      a.status === "closed"
                        ? "bg-emerald-50 text-emerald-700 ring-emerald-600/20"
                        : "bg-amber-50 text-amber-700 ring-amber-600/20"
                    }`}
                  >
                    {a.status === "closed" ? t("audit.closed") : t("audits_list.in_progress")}
                  </span>
                </TableCell>
                <TableCell>
                  {typeof a.score === "number" && a.score > 0 ? (
                    <SemaforoBadge score={a.score} />
                  ) : (
                    <span className="text-xs text-stone-400">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    {a.status === "closed" ? (
                      <>
                        <Link to={`/summary/${a.id}`}>
                          <Button size="sm" variant="outline" className="gap-1.5" data-testid={`view-summary-${a.id}`}>
                            <FileText size={14} /> {t("audits_list.view_summary")}
                          </Button>
                        </Link>
                        {canEdit && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => reopenAudit(a)}
                            className="gap-1.5"
                            data-testid={`reopen-audit-${a.id}`}
                            title={t("audits_list.reopen")}
                          >
                            <Edit3 size={14} /> {t("audits_list.edit_report")}
                          </Button>
                        )}
                      </>
                    ) : canContinue ? (
                      <Link to={`/audit/${a.id}`}>
                        <Button size="sm" className="brand-button gap-1.5" data-testid={`continue-audit-${a.id}`}>
                          {t("audits_list.continue")} <ChevronRight size={14} />
                        </Button>
                      </Link>
                    ) : (
                      <span className="text-xs text-stone-400">{t("audits_list.in_progress")}</span>
                    )}
                    {canDelete && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDeleting(a)}
                        className="text-stone-500 hover:text-red-600 hover:border-red-200"
                        title={t("audits_list.delete")}
                        data-testid={`delete-audit-${a.id}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <DialogContent className="max-w-md" data-testid="delete-audit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={18} />
              {t("audits_list.delete")}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-700 whitespace-pre-line" data-testid="delete-audit-message">
            {t("audits_list.confirm_delete", {
              store: deleting?.store_name || "",
              date: (deleting?.started_at || "").slice(0, 10),
            })}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)} disabled={busy}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={confirmDelete}
              disabled={busy}
              className="bg-red-600 hover:bg-red-700 text-white gap-1.5"
              data-testid="delete-audit-confirm"
            >
              <Trash2 size={14} />
              {t("audits_list.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
